import { createHttpClient } from '@shared/adapters/HttpClient';
import { Button } from '@shared/components/ui/button';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrdersApiAdapter } from '../../adapters/OrdersApiAdapter';

export interface UpdateFromStoreModalRef {
  open: (orderId: number, channelId?: number, onSuccess?: () => void) => void;
  close: () => void;
}

type UpdateFromStoreModalProps = Record<never, never>;

export const UpdateFromStoreModal = forwardRef<UpdateFromStoreModalRef, UpdateFromStoreModalProps>((_, ref) => {
  const { t } = useTranslation('ordersModule');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [channelId, setChannelId] = useState<number | undefined>(undefined);
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => void) | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const api = new OrdersApiAdapter(createHttpClient(apiClientWH));

  // Función helper para obtener el nombre del canal
  const getChannelName = (channelId?: number): string => {
    switch (channelId) {
      case 1:
        return 'Shopify';
      case 3:
        return 'WooCommerce';
      default:
        return t('confirmModals.updateFromStore.defaultChannel', {
          defaultValue: 'la tienda',
        });
    }
  };

  useImperativeHandle(ref, () => ({
    open: (orderId: number, channelId?: number, onSuccess?: () => void) => {
      setOrderId(orderId);
      setChannelId(channelId);
      setOnSuccessCallback(() => onSuccess);
      modalRef.current?.openModal();
    },
    close: () => {
      modalRef.current?.closeModal();
    },
  }));

  const handleConfirm = async () => {
    if (!orderId) {
      toast({
        title: t('toast.error.title'),
        description: 'ID de orden no disponible',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsLoading(true);
      await api.updateFromShop(orderId);
      toast({
        title: t('toast.update_store_success.title'),
        description: t('toast.update_store_success.description', {
          orderNumber: `#${orderId}`,
        }),
        variant: 'success',
      });
      modalRef.current?.closeModal();
      onSuccessCallback?.();
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        t('toast.error.description');
      toast({
        title: t('toast.error.title'),
        description: apiMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    modalRef.current?.closeModal();
  };

  return (
    <ModalCenter ref={modalRef} title={t('confirmModals.updateFromStoreModal.title')} type="default">
      <div className="space-y-4">
        <div className="space-y-3">
          <p className="text-sm">
            {t('confirmModals.updateFromStoreModal.message1', {
              channelName: getChannelName(channelId),
            })}
          </p>
          <p className="text-sm">{t('confirmModals.updateFromStoreModal.message2')}</p>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" size="lg" onClick={handleCancel} disabled={isLoading}>
            {t('buttons.cancel')}
            <EscIcon className="w-4 h-4" />
          </Button>
          <Button variant="primary" size="lg" onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? t('buttons.loading', 'Cargando...') : t('buttons.confirm')}
            <EnterIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

UpdateFromStoreModal.displayName = 'UpdateFromStoreModal';
